/**
 * PSP/CRE Tool v3.0 - Error Boundary
 * 
 * Catches React errors and provides graceful fallback UI
 * Prevents white screen of death
 * Offers recovery options (reload, reset)
 * 
 * Author: Dr. Tatar Radu MD
 * Date: November 20, 2025
 */

import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Alert,
  AlertTitle,
  Paper,
  Divider
} from '@mui/material';
import {
  ErrorOutline as ErrorIcon,
  Refresh as RefreshIcon,
  RestartAlt as ResetIcon,
  BugReport as BugIcon
} from '@mui/icons-material';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render shows fallback UI
    return { 
      hasError: true,
      error: error
    };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // Save error to localStorage for debugging
    try {
      const errorLog = {
        timestamp: new Date().toISOString(),
        message: error.toString(),
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        userAgent: navigator.userAgent,
        url: window.location.href
      };
      
      // Keep last 5 errors
      const savedErrors = JSON.parse(localStorage.getItem('psp_cre_errors') || '[]');
      savedErrors.push(errorLog);
      if (savedErrors.length > 5) {
        savedErrors.shift();
      }
      localStorage.setItem('psp_cre_errors', JSON.stringify(savedErrors));
      
    } catch (e) {
      console.error('Failed to save error log:', e);
    }
    
    // Update state with error info
    this.setState({
      errorInfo: errorInfo,
      errorCount: this.state.errorCount + 1
    });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleReset = () => {
    // Clear all data and reload
    try {
      localStorage.removeItem('psp_cre_scenario');
      localStorage.removeItem('psp_cre_scenario_timestamp');
      window.location.reload();
    } catch (e) {
      console.error('Failed to reset:', e);
      window.location.reload();
    }
  };

  handleCopyError = () => {
    const errorText = `
PSP/CRE Tool Error Report
=========================
Time: ${new Date().toISOString()}
Browser: ${navigator.userAgent}
URL: ${window.location.href}

Error Message:
${this.state.error?.toString() || 'Unknown error'}

Error Stack:
${this.state.error?.stack || 'No stack trace'}

Component Stack:
${this.state.errorInfo?.componentStack || 'No component stack'}
    `.trim();
    
    navigator.clipboard.writeText(errorText).then(() => {
      alert('Error details copied to clipboard. Please send to Dr. Tatar for debugging.');
    }).catch(err => {
      console.error('Failed to copy error:', err);
    });
  };

  render() {
    if (this.state.hasError) {
      return (
        <Container maxWidth="md" sx={{ mt: 8 }}>
          <Paper elevation={3} sx={{ p: 4 }}>
            {/* Error Icon and Title */}
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <ErrorIcon color="error" sx={{ fontSize: 80 }} />
              <Typography variant="h4" color="error" gutterBottom>
                Something Went Wrong
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                The application encountered an unexpected error
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Error Details */}
            <Alert severity="error" sx={{ mb: 3 }}>
              <AlertTitle>Error Details</AlertTitle>
              <Typography variant="body2" component="pre" sx={{ 
                overflow: 'auto', 
                maxHeight: 200,
                fontFamily: 'monospace',
                fontSize: '0.85rem'
              }}>
                {this.state.error?.toString() || 'Unknown error'}
              </Typography>
            </Alert>

            {/* Recovery Options */}
            <Typography variant="h6" gutterBottom>
              Recovery Options:
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
              {/* Option 1: Reload */}
              <Button
                variant="contained"
                color="primary"
                startIcon={<RefreshIcon />}
                onClick={this.handleReload}
                size="large"
                fullWidth
              >
                Reload Application
              </Button>
              
              {/* Option 2: Reset */}
              <Button
                variant="outlined"
                color="warning"
                startIcon={<ResetIcon />}
                onClick={this.handleReset}
                size="large"
                fullWidth
              >
                Reset to Default (Clears All Data)
              </Button>
              
              {/* Option 3: Copy Error */}
              <Button
                variant="outlined"
                startIcon={<BugIcon />}
                onClick={this.handleCopyError}
                size="large"
                fullWidth
              >
                Copy Error Details for Debugging
              </Button>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Troubleshooting Tips */}
            <Alert severity="info">
              <AlertTitle>Troubleshooting Tips</AlertTitle>
              <Typography variant="body2" component="ul" sx={{ mt: 1, mb: 0 }}>
                <li>Try reloading the page first (safest option)</li>
                <li>If error persists, reset to default scenario</li>
                <li>Check browser console (F12) for more details</li>
                <li>Ensure you're using a modern browser (Chrome, Firefox, Edge)</li>
                <li>Clear browser cache if issues continue</li>
              </Typography>
            </Alert>

            {/* Error Count Warning */}
            {this.state.errorCount > 1 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <AlertTitle>Multiple Errors Detected</AlertTitle>
                <Typography variant="body2">
                  This is error #{this.state.errorCount}. If errors keep occurring, 
                  please reset the application or contact Dr. Tatar.
                </Typography>
              </Alert>
            )}

            {/* Developer Info (only show in development) */}
            {process.env.NODE_ENV === 'development' && this.state.errorInfo && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <AlertTitle>Component Stack (Development Only)</AlertTitle>
                <Typography variant="body2" component="pre" sx={{ 
                  overflow: 'auto', 
                  maxHeight: 200,
                  fontFamily: 'monospace',
                  fontSize: '0.75rem'
                }}>
                  {this.state.errorInfo.componentStack}
                </Typography>
              </Alert>
            )}
          </Paper>
        </Container>
      );
    }

    // No error, render children normally
    return this.props.children;
  }
}

export default ErrorBoundary;
